import {
  ClassificationEntry,
  ShadowTestDiscrepancy,
  ShadowTestLine,
  ShadowTestMatchSet,
  ShadowTestResultResponse,
} from "../lusca-search-api/generated-src";

const escapeCSV = (val?: string | number | boolean | null) => {
  if (val == null) return "";
  const s = String(val);
  return s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};

const row = (...cells: (string | number | boolean | undefined | null)[]) =>
  cells.map(escapeCSV).join(",");

const LINE_HEADERS = [
  "Environment",
  "Journal Side",
  "Company Code",
  "Location",
  "Cost Center",
  "Account Number",
  "Product Line",
  "Channel",
  "Project",
  "Functional Amount",
  "Local Amount",
  "Currency",
  "GL Line Description",
  "Usecase",
  "Set of Books",
  "Match Key",
  "Classification",
];

const lineRow = (env: string, line: ShadowTestLine) =>
  row(
    env,
    line.journalSide,
    line.companyCode,
    line.location,
    line.costCenter,
    line.accountNumber,
    line.productLine,
    line.channel,
    line.project,
    line.functionalAmount,
    line.localAmount,
    line.currency,
    line.glLineDescription,
    line.usecase,
    line.setOfBooks,
    line.matchKey,
    line.classification
  );

export function exportShadowTestCSV(
  result: ShadowTestResultResponse,
  classifications: Record<string, ClassificationEntry>
) {
  const lines: string[] = [];

  // Summary metadata
  const testDate = result.testTimestamp
    ? new Date(result.testTimestamp).toISOString()
    : "";

  lines.push("Summary");
  lines.push(row("Transaction ID", result.transactionId));
  lines.push(row("GL Product Group", result.glProductGroup));
  lines.push(row("Country Code", result.countryCode));
  lines.push(row("Payment Provider", result.paymentProvider));
  lines.push(row("Promo", result.isPromo ? "Yes" : "No"));
  lines.push(row("Order Date", result.orderDate));
  lines.push(row("Test Result", result.passed ? "PASS" : "FAIL"));
  lines.push(row("Test Date", testDate));
  lines.push(row("Total Gamma Lines", result.totalGammaLines));
  lines.push(row("Total Prod Lines", result.totalProdLines));
  lines.push("");

  // Usecase comparison
  lines.push("Usecases Matched");
  lines.push(row("Environment", "Usecases"));
  if (result.gammaUsecases?.length) {
    lines.push(row("Gamma", result.gammaUsecases.join("; ")));
  }
  if (result.prodUsecases?.length) {
    lines.push(row("Prod", result.prodUsecases.join("; ")));
  }
  if (result.jemUsecases?.length) {
    lines.push(row("JEM", result.jemUsecases.join("; ")));
  }
  lines.push("");

  // Match Sets
  if (result.matchSets?.length) {
    lines.push("Match Sets");
    lines.push(row("Environment", "Match Key", "Account Number", "Amount", "Currency", "Classification", "Closed"));
    for (const ms of result.matchSets) {
      lines.push(row(ms.environment, ms.matchKey, ms.accountNumber, ms.amount, ms.currency, ms.classification, ms.closed));
    }
    lines.push("");
  }

  // Matched Lines
  if (result.matchedLines?.length) {
    lines.push("Matched USL Lines");
    lines.push(row(...LINE_HEADERS));
    for (const l of result.matchedLines) {
      lines.push(lineRow(l.environment || "matched", l));
    }
    lines.push("");
  }

  // Unique Lines
  const uniqueSections: [string, ShadowTestLine[] | undefined][] = [
    ["JEM Unique Lines", result.jemUniqueLines],
    ["Gamma Unique Lines", result.gammaUniqueLines],
    ["Prod Unique Lines", result.prodUniqueLines],
  ];
  for (const [title, items] of uniqueSections) {
    if (items?.length) {
      lines.push(title);
      lines.push(row(...LINE_HEADERS));
      for (const l of items) {
        lines.push(lineRow(l.environment || title.split(" ")[0], l));
      }
      lines.push("");
    }
  }

  // Discrepancies
  if (result.discrepancies?.length) {
    lines.push("Discrepancies");
    lines.push(row("Usecase", "Field", "Gamma Value", "Prod Value", "JEM Value", "Account Number", "Classification"));
    for (const d of result.discrepancies) {
      lines.push(row(d.usecase, d.field, d.gammaValue, d.prodValue, d.jemValue, d.accountNumber, d.classification));
    }
    lines.push("");
  }

  // Classification codes legend
  const classEntries = Object.values(classifications);
  if (classEntries.length) {
    lines.push("Classification Codes");
    lines.push(row("Code", "Description"));
    for (const c of classEntries) {
      lines.push(row(c.code, c.description));
    }
  }

  return lines.join("\n");
}

export function downloadShadowTestCSV(
  result: ShadowTestResultResponse,
  classifications: Record<string, ClassificationEntry>
) {
  const csv = exportShadowTestCSV(result, classifications);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const filename = `shadow-test-${result.transactionId}-${result.glProductGroup || "unknown"}-${result.countryCode || "unknown"}-${ts}.csv`;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
